<script setup>
import { ref, computed } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import Modal from 'dashboard/components/Modal.vue';

const props = defineProps({
  show: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['update:show', 'chatCreated']);

const show = computed({
  get: () => props.show,
  set: val => emit('update:show', val),
});

const store = useStore();
const { t } = useI18n();
const { showAlert } = useAlert();

const name = ref('');
const description = ref('');
const isCreating = ref(false);

const canCreate = computed(() => name.value.trim().length > 0);

const submit = async () => {
  if (!canCreate.value || isCreating.value) return;
  isCreating.value = true;
  try {
    const chat = await store.dispatch('internalChats/createChannel', {
      name: name.value.trim(),
      description: description.value.trim(),
    });
    emit('chatCreated', chat);
    show.value = false;
  } catch {
    showAlert(t('INTERNAL_CHATS.TOAST.ERROR_GENERIC'));
  } finally {
    isCreating.value = false;
  }
};

const onClose = () => {
  name.value = '';
  description.value = '';
};
</script>

<template>
  <Modal v-model:show="show" @close="onClose">
    <div class="p-6 flex flex-col gap-4">
      <h3 class="text-base font-semibold text-n-slate-12">
        {{ t('INTERNAL_CHATS.NEW_CHANNEL_TITLE') }}
      </h3>

      <div class="flex flex-col gap-1">
        <label
          class="text-xs font-medium text-n-slate-11"
          for="channel-name-input"
        >
          {{ t('INTERNAL_CHATS.CHANNEL_NAME_LABEL') }}
        </label>
        <div
          class="flex items-center gap-1.5 rounded-lg border border-n-weak bg-n-background px-3 py-2 focus-within:border-n-brand"
        >
          <span class="i-lucide-hash size-3.5 text-n-slate-9 flex-shrink-0" />
          <input
            id="channel-name-input"
            v-model="name"
            type="text"
            maxlength="80"
            :placeholder="t('INTERNAL_CHATS.CHANNEL_NAME_PLACEHOLDER')"
            class="w-full bg-transparent text-sm text-n-slate-12 outline-none placeholder:text-n-slate-9"
            @keydown.enter="submit"
          />
        </div>
      </div>

      <div class="flex flex-col gap-1">
        <label
          class="text-xs font-medium text-n-slate-11"
          for="channel-description-input"
        >
          {{ t('INTERNAL_CHATS.GROUP_DESCRIPTION_LABEL') }}
        </label>
        <textarea
          id="channel-description-input"
          v-model="description"
          maxlength="280"
          :placeholder="t('INTERNAL_CHATS.GROUP_DESCRIPTION_PLACEHOLDER')"
          rows="2"
          class="w-full rounded-lg border border-n-weak bg-n-background px-3 py-2 text-sm text-n-slate-12 outline-none focus:border-n-brand placeholder:text-n-slate-9 resize-none"
        />
      </div>

      <p class="flex items-start gap-1.5 text-xs text-n-slate-9">
        <span class="i-lucide-users size-3.5 flex-shrink-0 mt-0.5" />
        {{ t('INTERNAL_CHATS.CHANNEL_ALL_MEMBERS_HINT') }}
      </p>

      <button
        :disabled="!canCreate || isCreating"
        class="w-full rounded-lg bg-n-brand px-4 py-2 text-sm font-medium text-white transition-opacity disabled:opacity-50 disabled:cursor-not-allowed hover:opacity-90 flex items-center justify-center gap-2"
        @click="submit"
      >
        <span
          v-if="isCreating"
          class="i-lucide-loader-2 size-3.5 animate-spin"
        />
        {{ t('INTERNAL_CHATS.CREATE_CHANNEL') }}
      </button>
    </div>
  </Modal>
</template>
